import { Question } from '@phosphor-icons/react'
import { Form, Select } from 'antd'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { useUpdateLockedQuestionsByFormId } from '@/queries/measures/useUpdateLockedQuestionsByFormId'
import { useLoadQuestionByResourceId } from '@/queries/questions/useLoadQuestionByResourceId'
import Tooltip from '@/ui/atoms/Tooltip'
import FormAntd from '@/ui/form/antd/FormAntd'
import ToggleField from '@/ui/molecules/ToggleField'

import {
  ConfirmLockingFormModalModel,
  useConfirmLockingFormModal,
} from '../ConfirmLockingFormModal/useConfirmLockingFormModal'
import { areArraysEqual } from './areArraysEqual'

export const ChangeMeasuresTracking = () => {
  const params = useParams<{ resourceId?: string }>()
  const { form, rule } = useConfirmLockingFormModal()

  const { data: allQuestionsData, isLoading: isLoadingAllQuestions } =
    useLoadQuestionByResourceId({
      form: String(params?.resourceId || ''),
    })
  const { data: trackedQuestionsData, isLoading: isLoadingTrackedQuestions } =
    useLoadQuestionByResourceId({
      form: String(params?.resourceId || ''),
      tracked_only: true,
    })
  const {
    updateLockedQuestionsByFormId,
    isLoading: isLoadingUpdateLockedQuestions,
  } = useUpdateLockedQuestionsByFormId({
    formId: String(params?.resourceId || ''),
    tracked_only: true,
  })

  const onFormSubmit = async (data: ConfirmLockingFormModalModel) => {
    const oldValues = trackedQuestionsData?.map((item) => item.id) || []
    const newValues = data.questions as number[]
    if (areArraysEqual(newValues, oldValues)) return
    try {
      await updateLockedQuestionsByFormId({
        formId: String(params?.resourceId || ''),
        data,
      })
      toast.info('The track questions were updated successfully')
    } catch (error) {
      toast.error('Error while update track questions')
    }
  }

  const questionOptions = allQuestionsData
    ?.filter((item) => ['calculation', 'number'].includes(item.subtype))
    ?.map((item, index) => ({
      label: `${index + 1}. ${item.text}`,
      value: item.id,
    }))

  let initialValues = {}

  if (trackedQuestionsData?.length) {
    initialValues = {
      questions: trackedQuestionsData?.map((item) => item.id),
    }
  }

  return (
    <div className="relative flex w-full flex-col gap-5">
      <div className="w-full">
        <ToggleField
          compact
          disabled
          horizontal
          primary
          label="Lock Form for Measures Tracking"
          value={true}
        />
      </div>
      <Tooltip
        mouseEnterDelay={0.4}
        title="You cannot change this option after locking the form"
      >
        <Question className="absolute -top-1 right-16 rounded-full bg-primary p-1 text-3xl text-white" />
      </Tooltip>
      <div className="relative flex w-full flex-col gap-3 rounded bg-primary/10 p-3">
        <FormAntd.Root<ConfirmLockingFormModalModel>
          className="mb-4 w-full"
          form={form}
          initialValues={initialValues}
          key={trackedQuestionsData?.length}
          layout="vertical"
          onFinish={onFormSubmit}
          validateTrigger={['onBlur']}
        >
          <Form.Item<ConfirmLockingFormModalModel>
            className="w-full [&_label]:!text-base [&_label]:font-bold"
            label="Tracked Questions"
            name="questions"
            rules={[rule]}
          >
            <Select
              allowClear
              className="w-full"
              disabled={isLoadingTrackedQuestions || isLoadingAllQuestions}
              loading={
                isLoadingUpdateLockedQuestions ||
                isLoadingTrackedQuestions ||
                isLoadingAllQuestions
              }
              maxTagCount="responsive"
              mode="multiple"
              onBlur={() => form.submit()}
              options={questionOptions || []}
              placeholder="Select questions"
            />
          </Form.Item>
        </FormAntd.Root>
      </div>
    </div>
  )
}
